/**
 * Web Bluetooth readiness checks for MindOS.
 *
 * MindOS talks to the ela headband through `navigator.bluetooth`. That API
 * only exists in Chromium browsers (Chrome, Edge, Opera) on Windows, macOS,
 * Linux, ChromeOS and Android, and only on a secure origin (HTTPS or
 * localhost). These checks let the dashboard explain what is missing before
 * the user opens MindOS, instead of MindOS failing silently.
 */

/** Minimal typing: Web Bluetooth is not part of TypeScript's DOM lib. */
interface BluetoothLike {
  requestDevice?: unknown;
  getAvailability?: () => Promise<boolean>;
}

export type BluetoothSupport =
  | { status: 'ready' }
  | { status: 'adapter-off' }
  | { status: 'insecure-context' }
  | { status: 'ios' }
  | { status: 'unsupported-browser' };

function bluetoothApi(): BluetoothLike | undefined {
  return (navigator as Navigator & { bluetooth?: BluetoothLike }).bluetooth;
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export async function checkBluetoothSupport(): Promise<BluetoothSupport> {
  if (!window.isSecureContext) return { status: 'insecure-context' };

  const api = bluetoothApi();
  if (!api || typeof api.requestDevice !== 'function') {
    return isIOS() ? { status: 'ios' } : { status: 'unsupported-browser' };
  }

  // getAvailability() reports whether a Bluetooth adapter is present and on.
  // It is optional and can throw under some enterprise policies; treat any
  // failure as "unknown" and let the browser's own device chooser decide.
  if (typeof api.getAvailability === 'function') {
    try {
      if (!(await api.getAvailability())) return { status: 'adapter-off' };
    } catch {
      /* unknown — fall through */
    }
  }

  return { status: 'ready' };
}

export function readNoticeAccepted(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function writeNoticeAccepted(key: string, accepted: boolean): void {
  try {
    if (accepted) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch {
    /* storage blocked — the notice simply shows again next time */
  }
}
